"use client";

import type { ReactNode } from "react";
import { useState } from "react";
import Image from "next/image";
import {
  Mail,
  MessageCircle,
  MessagesSquare,
  Phone,
  Video,
  type LucideIcon,
} from "lucide-react";
import { SiFacebook, SiWhatsapp } from "react-icons/si";
import {
  SITEGLUE_MARKETING_CHANNELS,
  type SiteGlueMarketingChannelId,
} from "~/lib/siteglue/communication-channels";
import { defaultAvatarUrlForAgentName } from "~/lib/siteglue/ai-employee-role-catalog";
import { SITEGLUE_COLORS } from "~/lib/siteglue/marketing-chrome";
import { cn } from "~/lib/utils";

const CHANNEL_ICONS: Record<
  SiteGlueMarketingChannelId,
  LucideIcon | typeof SiWhatsapp | typeof SiFacebook
> = {
  website_chat: MessageCircle,
  phone: Phone,
  sms: MessagesSquare,
  email: Mail,
  video_calls: Video,
  whatsapp: SiWhatsapp,
  messenger: SiFacebook,
};

/** Marketing channels for homepage signature demos — from shared SiteGlue config. */
export const HOME_SIGNATURE_CHANNELS = SITEGLUE_MARKETING_CHANNELS.map((ch) => ({
  id: ch.id,
  label: ch.label,
  Icon: CHANNEL_ICONS[ch.id],
  description: ch.description,
  takeover: ch.takeover,
}));

export type HomeAgentSignatureProps = {
  agentName: string;
  agentTitle: string;
  companyName: string;
  avatarSrc: string;
  accentColor?: string;
  phoneDisplay?: string;
  emailAddress?: string;
  websiteDisplay?: string;
  className?: string;
  /** Light card on dark hero, or slate card on light sections. */
  tone?: "dark" | "light";
  /** Career level badge (e.g. Intern). Optional — role pages omit. */
  careerLevel?: string;
  /**
   * Channels marked Active. When omitted, all channels show Active (legacy role-page demos).
   * Intern marketing examples should pass `["website_chat"]` only.
   */
  activeChannelIds?: SiteGlueMarketingChannelId[];
  /** Optional industry label under the company name. */
  industryLabel?: string;
  /** Optional short responsibility list under channels. */
  responsibilities?: string[];
  /** Label for channels not yet active on this agent (default: Associate+). */
  inactiveChannelLabel?: string;
  /** Optional footer (e.g. CTA) — replaces responsibility bullets when set. */
  footer?: ReactNode;
};

export function HomeAgentSignature({
  agentName,
  agentTitle,
  companyName,
  avatarSrc,
  accentColor = SITEGLUE_COLORS.blue,
  phoneDisplay,
  emailAddress,
  websiteDisplay,
  className,
  tone = "light",
  careerLevel,
  activeChannelIds,
  industryLabel,
  responsibilities,
  inactiveChannelLabel = "Associate+",
  footer,
}: HomeAgentSignatureProps) {
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [failedForSrc, setFailedForSrc] = useState<string | null>(null);
  const fallbackAvatar = defaultAvatarUrlForAgentName(
    agentName.split(/\s+/)[0] ?? agentName,
  );
  const srcFailed = avatarFailed && failedForSrc === avatarSrc;
  const resolvedAvatarSrc =
    srcFailed || !avatarSrc.trim() ? fallbackAvatar : avatarSrc;

  const email =
    emailAddress ??
    (websiteDisplay
      ? `${agentName.split(" ")[0]?.toLowerCase() ?? "agent"}@${websiteDisplay.replace(/^www\./, "")}`
      : `${agentName.split(" ")[0]?.toLowerCase() ?? "agent"}@example.com`);

  const isDark = tone === "dark";
  const restrictChannels = Array.isArray(activeChannelIds);
  const activeSet = new Set(activeChannelIds ?? []);
  const phoneCallsEnabled = restrictChannels ? activeSet.has("phone") : true;
  const directLineText = (() => {
    if (restrictChannels) {
      if (!phoneCallsEnabled) return "Disabled";
      return phoneDisplay?.trim() || "Not configured";
    }
    return phoneDisplay ?? "(555) 010-2000";
  })();
  const showAiReceptionistNote =
    phoneCallsEnabled && directLineText !== "Disabled" && directLineText !== "Not configured";

  return (
    <div
      className={cn(
        "rounded-2xl border p-4 shadow-sm sm:p-5",
        isDark
          ? "border-white/15 bg-white/[0.07] text-white shadow-black/30 backdrop-blur-sm"
          : "border-slate-200 bg-white text-slate-900",
        className,
      )}
    >
      <div className="flex items-start gap-3.5">
        <div
          className={cn(
            "relative h-[70px] w-[70px] shrink-0 overflow-hidden rounded-full ring-2",
            isDark ? "ring-white/20" : "ring-slate-200",
          )}
        >
          <Image
            key={resolvedAvatarSrc}
            src={resolvedAvatarSrc}
            alt={agentName}
            fill
            className="object-cover object-top"
            sizes="70px"
            onError={() => {
              setFailedForSrc(avatarSrc);
              setAvatarFailed(true);
            }}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-base font-semibold leading-tight" style={{ color: accentColor }}>
              {agentName}
            </p>
            {careerLevel ? (
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                  isDark ? "bg-white/10 text-slate-200" : "bg-slate-100 text-slate-600",
                )}
              >
                {careerLevel}
              </span>
            ) : null}
          </div>
          <p className={cn("mt-0.5 text-xs", isDark ? "text-slate-300" : "text-slate-500")}>
            {agentTitle}
          </p>
          <p className={cn("mt-1 text-sm font-bold", isDark ? "text-white" : "text-slate-900")}>
            {companyName}
          </p>
          {industryLabel ? (
            <p className={cn("mt-0.5 text-[11px]", isDark ? "text-slate-400" : "text-slate-500")}>
              {industryLabel}
            </p>
          ) : null}
        </div>
      </div>

      <div className={cn("mt-3 space-y-1 text-xs leading-snug", isDark ? "text-slate-200" : "text-slate-700")}>
        <p>
          <span className="font-bold" style={{ color: accentColor }}>
            Direct Line:
          </span>{" "}
          {directLineText}{" "}
          {showAiReceptionistNote ? (
            <span className={isDark ? "text-slate-400" : "text-slate-500"}>
              (AI Receptionist Enabled)
            </span>
          ) : null}
        </p>
        <p>
          <span className="font-bold" style={{ color: accentColor }}>
            Email:
          </span>{" "}
          <span className="break-all">{email}</span>
        </p>
        {websiteDisplay ? (
          <p>
            <span className="font-bold" style={{ color: accentColor }}>
              Website:
            </span>{" "}
            {websiteDisplay}
          </p>
        ) : null}
      </div>

      <ul
        className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 border-t pt-3 sm:grid-cols-2"
        style={{ borderColor: isDark ? "rgba(255,255,255,0.12)" : "#e2e8f0" }}
      >
        {HOME_SIGNATURE_CHANNELS.map(({ id, label, Icon }) => {
          const isActive = restrictChannels ? activeSet.has(id) : true;
          return (
            <li
              key={label}
              className={cn(
                "flex items-center gap-1.5 text-[11px] font-medium",
                isActive
                  ? isDark
                    ? "text-slate-200"
                    : "text-slate-700"
                  : isDark
                    ? "text-slate-500"
                    : "text-slate-400",
              )}
            >
              <Icon
                className="h-3 w-3 shrink-0"
                style={{ color: isActive ? accentColor : undefined }}
                aria-hidden
              />
              <span className="truncate">{label}</span>
              <span
                className={cn(
                  "ml-auto shrink-0 text-[9px] font-semibold leading-tight sm:text-[10px]",
                  isActive
                    ? "text-emerald-500"
                    : isDark
                      ? "text-slate-500"
                      : "text-slate-400",
                )}
                title={isActive ? undefined : "Not enabled on this agent"}
              >
                {isActive ? "Active" : inactiveChannelLabel}
              </span>
            </li>
          );
        })}
      </ul>

      {footer ? (
        <div className="mt-3 border-t pt-3" style={{ borderColor: isDark ? "rgba(255,255,255,0.12)" : "#e2e8f0" }}>
          {footer}
        </div>
      ) : responsibilities && responsibilities.length > 0 ? (
        <ul
          className={cn(
            "mt-3 space-y-1 border-t pt-3 text-[11px] leading-snug",
            isDark ? "border-white/12 text-slate-300" : "border-slate-200 text-slate-600",
          )}
        >
          {responsibilities.slice(0, 3).map((item) => (
            <li key={item} className="truncate">
              · {item}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
